import { createContext, Dispatch, ReactNode, SetStateAction, useCallback, useContext, useEffect, useState } from "react";
import { useLocation } from "react-router-dom";
import { toast } from "react-toastify";
import { PagesPaginateModel } from "../../models/Paginate/PagesPaginate";
import { ToastSettings } from "../../utils/ToastSettings";
import { ArticlesCommentsService } from "../../services/Articles/ArticlesCommentsService";

interface ContextProps {
    articleComment: any,
    setArticleComment: Dispatch<SetStateAction<any>>,
    articleComments: any[],
    articlesLastUpdates: any[],
    params: any,
    setParams: Dispatch<SetStateAction<any>>,
    pages: PagesPaginateModel,
    setPages: Dispatch<SetStateAction<PagesPaginateModel>>,
    isLoading: boolean,
    error: string,
    setError: Dispatch<SetStateAction<string>>,
    handleList: () => void,
    handleFetch: (data: any) => Promise<boolean>,
    handleDelete: (id: number) => Promise<boolean>,
    handleListUpdates: () => Promise<boolean>,
}

export const ArticleCommentContext = createContext<ContextProps>({} as ContextProps);

interface ArticleCommentProviderProps {
    children: ReactNode;
}

export const ArticleCommentProvider = ({ children }: ArticleCommentProviderProps) => {
    const [articleComment, setArticleComment] = useState<any>({} as any);
    const [articleComments, setArticleComments] = useState<any[]>([]);
    const [articlesLastUpdates, setArticlesLastUpdates] = useState<any[]>([]);
    const [params, setParams] = useState<any>(null);
    const [pages, setPages] = useState<PagesPaginateModel>({});
    const [isLoading, setIsLoading] = useState<boolean>(true);
    const [error, setError] = useState<string>('');

    const service = new ArticlesCommentsService();
    const location = useLocation();

    const handleList = useCallback(async () => {
        if (!params || !location.pathname.includes('/articles/view')) {
            return false;
        }

        try {
            setIsLoading(true);
            const [_Response, _Error] = await service.list(params);
            setIsLoading(false);

            if (!!_Error) {
                return setError(_Response?.message || _Error);
            }

            setArticleComments(_Response?.items);
            setPages(_Response?.pagination);
            return setError('');
        } catch (e) {
            setIsLoading(false);
            setError('Não foi possível carregar os comentários do artigo.');
        }
    }, [params, location.pathname]);

    const handleFetch = async (data: any) => {
        try {
            const [_Response, _Error, _Code, _Errors] = await service.fetch(data.article_id, data.comment, data?.id || null);
            if (!!_Error) {
                ToastSettings(_Response?.message || _Error, 'bottom-center', 'error', () => { }, _Errors);
                return false;
            }

            setError('');
            return true;
        } catch (e) {
            setError('Houve um erro ao salvar o comentário');
            return false;
        }
    }

    const handleDelete = async (id: number) => {
        try {
            setIsLoading(true);
            const [_Response, _Error] = await service.delete(id);
            setIsLoading(false);

            if (!!_Error) {
                setError(_Response?.message || _Error);
                return false;
            }

            setError('');
            return true;
        } catch (e: any) {
            setIsLoading(false);
            setError(e.response?.data?.message || 'Houve um erro ao deletar o comentário');
            return false;
        }
    }

    const handleListUpdates = async () => {
        try {
            setIsLoading(true);
            const [_Response, _Error] = await service.listUpdates();
            setIsLoading(false);

            if (!!_Error) {
                setError(_Response?.message || _Error);
                return false;
            }

            setError('');
            setArticlesLastUpdates(_Response?.data?.items);
            return true;
        } catch (e: any) {
            setIsLoading(false);
            setError(e.response?.data?.message || 'Houve um erro ao listar os últimos artigos atualizados');
            return false;
        }
    }

    useEffect(() => {
        !!error && toast.error(error);
    }, [error]);

    useEffect(() => {
        handleList().then();
    }, [handleList]);

    return (
        <ArticleCommentContext.Provider value={{
            articleComment,
            setArticleComment,
            articleComments,
            articlesLastUpdates,
            params,
            setParams,
            pages,
            setPages,
            isLoading,
            error,
            setError,
            handleList,
            handleFetch,
            handleDelete,
            handleListUpdates
        }}>
            {children}
        </ArticleCommentContext.Provider>
    );
}

export const useArticleComment = () => useContext(ArticleCommentContext);